import React, { useEffect, useRef } from "react";
import { BaseIndicatorProps } from "../../lib/interface/Indicator.interface";
import { LineDto } from "../../lib/interface/LineDto.interface";
import { ISeriesApi, LineSeries, Time } from "lightweight-charts";
import { INDICATOR_COLORS } from "../../lib/constants/chart.constants";
import { debugLog } from "@/lib/utils/debug";

/**
 * Props specific to Support/Resistance indicator
 */
export interface SupportResistanceProps extends BaseIndicatorProps {
  supLines: LineDto[];
  resLines: LineDto[];
  formatTime: (timestamp: number) => number;
}

/**
 * Support/Resistance Indicator Component
 * Shows support and resistance lines on the chart
 * Manages its own series creation and cleanup based on isEnabled prop
 */
export const SupportResistanceIndicator: React.FC<SupportResistanceProps> = ({
  chartInstance,
  isEnabled,
  supLines,
  resLines,
  formatTime,
}) => {
  const supportSeriesRef = useRef<ISeriesApi<"Line">[]>([]);
  const resistanceSeriesRef = useRef<ISeriesApi<"Line">[]>([]);

  useEffect(() => {
    if (!chartInstance) return;

    // Clear existing support series
    supportSeriesRef.current.forEach((series) => {
      try {
        chartInstance.removeSeries(series);
      } catch {
        // Silently ignore errors
      }
    });
    supportSeriesRef.current = [];

    // Clear existing resistance series
    resistanceSeriesRef.current.forEach((series) => {
      try {
        chartInstance.removeSeries(series);
      } catch {
        // Silently ignore errors
      }
    });
    resistanceSeriesRef.current = [];

    // Only create new series if enabled and we have data
    if (isEnabled) {
      // Create support lines
      if (supLines.length > 0) {
        debugLog("SupportResistance: creating", supLines.length, "support lines");

        const newSupportSeries = supLines.map((line) => {
          const series = chartInstance.addSeries(LineSeries, {
            color: INDICATOR_COLORS.support,
            lineWidth: 2,
            lineStyle: 1,
          });

          series.setData([
            {
              time: formatTime(line.timeStart) as Time,
              value: Number(line.priceStart),
            },
            {
              time: formatTime(line.timeEnd) as Time,
              value: Number(line.priceEnd),
            },
          ]);

          return series;
        });

        supportSeriesRef.current = newSupportSeries;
      }

      // Create resistance lines
      if (resLines.length > 0) {
        debugLog(
          "SupportResistance: creating",
          resLines.length,
          "resistance lines"
        );

        const newResistanceSeries = resLines.map((line) => {
          const series = chartInstance.addSeries(LineSeries, {
            color: INDICATOR_COLORS.resistance,
            lineWidth: 2,
            lineStyle: 1,
          });

          series.setData([
            {
              time: formatTime(line.timeStart) as Time,
              value: Number(line.priceStart),
            },
            {
              time: formatTime(line.timeEnd) as Time,
              value: Number(line.priceEnd),
            },
          ]);

          return series;
        });

        resistanceSeriesRef.current = newResistanceSeries;
      }

      debugLog("SupportResistance: series updated");
    } else {
      debugLog("SupportResistance: disabled");
    }

    // Cleanup function
    return () => {
      supportSeriesRef.current.forEach((series) => {
        try {
          chartInstance.removeSeries(series);
        } catch {
          // Silently ignore cleanup errors
        }
      });
      supportSeriesRef.current = [];

      resistanceSeriesRef.current.forEach((series) => {
        try {
          chartInstance.removeSeries(series);
        } catch {
          // Silently ignore cleanup errors
        }
      });
      resistanceSeriesRef.current = [];
    };
  }, [chartInstance, supLines, resLines, isEnabled, formatTime]);

  // This is a logic-only component that manages chart series
  return null;
};
